import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ArrowLeft, ArrowRight, Loader2, Save, CheckCircle2, AlertCircle,
  Users, UserPlus, Trash2, Skull, Sparkles, Hammer, FileText, Lock,
  Eye, X, Mail, Upload, ImageIcon as LogoIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "@/i18n";

/*  Case Maker — public $12 wizard
    Flow:
      1. Pick engine (3 cards)
      2. Setup slots (engine-specific)
      3. Players (4-12, mark one culprit) + fairness check
      4. Preview + checkout → Stripe → /make/download/{order_id}

    Draft is autosaved every ~1s to the backend so refreshes are safe.
    draft_id is stashed in localStorage so a half-finished wizard resumes.
*/

const STORAGE_KEY = "wasntme.make.draft";
const STEPS = ["engine", "setup", "players", "preview"];

const Make = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const resumeId = params.get("draft") || null;

  const [engines, setEngines] = useState([]);
  const [bootLoading, setBootLoading] = useState(true);
  const [draftId, setDraftId] = useState(null);
  const [step, setStep] = useState(0);

  // Draft state
  const [engineId, setEngineId] = useState("");
  const [title, setTitle] = useState("");
  const [theme, setTheme] = useState("");
  const [victimName, setVictimName] = useState("");
  const [hostName, setHostName] = useState("");
  const [hostEmail, setHostEmail] = useState("");
  const [slots, setSlots] = useState({});
  const [players, setPlayers] = useState([]);

  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState("");
  const saveTimer = useRef(null);

  const engine = useMemo(
    () => engines.find((e) => e.id === engineId),
    [engines, engineId],
  );

  // ----- Boot: load engines, resume if draft id present -----
  useEffect(() => {
    api.get("/make/engines").then((r) => {
      setEngines(r.data.engines || []);
    }).catch(() => toast.error("Couldn't load engines."));
    const stashed = resumeId || localStorage.getItem(STORAGE_KEY);
    if (stashed) {
      api.get(`/make/draft/${stashed}`).then((r) => {
        const d = r.data.draft || {};
        setDraftId(d.draft_id);
        setEngineId(d.engine_id || "");
        setTitle(d.title || "");
        setTheme(d.theme || "");
        setVictimName(d.victim_name || "");
        setHostName(d.host_name || "");
        setHostEmail(d.host_email || "");
        setSlots(d.engine_slots || {});
        setPlayers(d.players || []);
        if (d.engine_id) setStep(1);
      }).catch(() => {
        localStorage.removeItem(STORAGE_KEY);
      }).finally(() => setBootLoading(false));
    } else {
      setBootLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- Autosave (debounced 1s) -----
  const scheduleSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        const { data } = await api.post("/make/draft", {
          draft_id: draftId, engine_id: engineId, title, theme,
          victim_name: victimName, host_name: hostName, host_email: hostEmail,
          players, engine_slots: slots,
        });
        if (data.draft_id && data.draft_id !== draftId) {
          setDraftId(data.draft_id);
          localStorage.setItem(STORAGE_KEY, data.draft_id);
        }
        setLastSavedAt(data.updated_at);
      } catch (err) {
        if (err?.response?.status !== 429) toast.error("Autosave failed.");
      } finally {
        setSaving(false);
      }
    }, 1000);
  }, [draftId, engineId, title, theme, victimName, hostName, hostEmail, players, slots]);

  useEffect(() => {
    if (bootLoading) return;
    if (!engineId && !title && !players.length) return; // nothing to save yet
    scheduleSave();
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [bootLoading, engineId, title, theme, victimName, hostName, hostEmail, slots, players, scheduleSave]);

  // ----- Players mutations -----
  const addPlayer = () => {
    if (players.length >= 12) return;
    setPlayers([...players, {
      name: "", role: "", secret: "", signature_line: "",
      is_culprit: false, where_when: "", has_access: "", attr_1_value: "",
      attr_2_value: "", attr_3_value: "", looks_suspicious: "",
    }]);
  };
  const updatePlayer = (i, patch) => {
    setPlayers(players.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  };
  const removePlayer = (i) => setPlayers(players.filter((_, idx) => idx !== i));
  const markCulprit = (i) => setPlayers(players.map((p, idx) => ({ ...p, is_culprit: idx === i })));

  if (bootLoading) {
    return <div className="grain min-h-screen flex items-center justify-center">
      <Loader2 size={28} className="animate-spin text-[#8b0000]" />
    </div>;
  }

  return (
    <div className="grain min-h-screen" data-testid="make-page">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#a39797] hover:text-[#d4af37]" data-testid="make-back">
            <ArrowLeft size={16} /> {t("common.backToFile")}
          </Link>
          <SaveIndicator saving={saving} savedAt={lastSavedAt} t={t} />
        </div>

        {/* Header + stepper */}
        <div className="mb-8">
          <div className="font-hand text-[#d4af37] rotate-[-1deg] text-lg mb-2">{t("make.sectionLabel")}</div>
          <h1 className="font-display text-4xl sm:text-5xl text-[#f4f0ec] mb-2">{t("make.title")}</h1>
          <p className="text-[#a39797] text-sm max-w-xl mb-5">
            {t("make.intro")}
          </p>
          <Stepper step={step} t={t} />
        </div>

        {step === 0 && (
          <EnginePicker
            engines={engines}
            selected={engineId}
            onPick={(id) => { setEngineId(id); setStep(1); }}
            t={t}
          />
        )}

        {step === 1 && engine && (
          <SetupStep
            engine={engine}
            title={title} setTitle={setTitle}
            theme={theme} setTheme={setTheme}
            victimName={victimName} setVictimName={setVictimName}
            hostName={hostName} setHostName={setHostName}
            hostEmail={hostEmail} setHostEmail={setHostEmail}
            slots={slots} setSlots={setSlots}
            onBack={() => setStep(0)}
            onNext={() => setStep(2)}
            t={t}
          />
        )}

        {step === 2 && engine && (
          <PlayersStep
            engine={engine}
            players={players}
            addPlayer={addPlayer}
            updatePlayer={updatePlayer}
            removePlayer={removePlayer}
            markCulprit={markCulprit}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
            draftId={draftId}
            t={t}
          />
        )}

        {step === 3 && draftId && (
          <PreviewStep
            draftId={draftId}
            engine={engine}
            hostName={hostName} hostEmail={hostEmail}
            onBack={() => setStep(2)}
            t={t}
          />
        )}
      </div>
    </div>
  );
};

// ---------- Save indicator ----------
const SaveIndicator = ({ saving, savedAt, t }) => (
  <div className="text-[11px] text-[#a39797] inline-flex items-center gap-1">
    {saving ? (
      <><Loader2 size={10} className="animate-spin" /> {t("make.savingDraft")}</>
    ) : savedAt ? (
      <><Save size={10} /> {t("make.draftSaved")}</>
    ) : null}
  </div>
);

// ---------- Stepper ----------
const Stepper = ({ step, t }) => {
  const labels = [t("make.stepEngine"), t("make.stepSetup"), t("make.stepPlayers"), t("make.stepPreview")];
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="make-stepper">
      {labels.map((l, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
            i === step ? "bg-[#8b0000] text-[#f4f0ec]" :
            i < step ? "bg-[#2d4f2d] text-[#9fd79f]" : "bg-[#1a1413] text-[#5a5050] border border-[#2d2424]"
          }`}>{i < step ? "✓" : i + 1}</div>
          <div className={`text-xs tracking-widest uppercase ${
            i === step ? "text-[#f4f0ec]" : i < step ? "text-[#9fd79f]" : "text-[#5a5050]"
          }`}>{l}</div>
          {i < 3 && <div className="w-6 h-px bg-[#2d2424]" />}
        </div>
      ))}
    </div>
  );
};

// ---------- Step 1: Engine picker ----------
const EnginePicker = ({ engines, selected, onPick, t }) => (
  <div data-testid="make-step-engine">
    <h2 className="font-display text-2xl text-[#f4f0ec] mb-5">{t("make.engineHeader")}</h2>
    <div className="grid md:grid-cols-3 gap-4">
      {engines.map((e) => (
        <button
          key={e.id}
          onClick={() => onPick(e.id)}
          className={`card-noir p-5 text-left hover:border-[#d4af37] transition-colors ${
            selected === e.id ? "border-[#d4af37]" : ""
          }`}
          data-testid={`engine-${e.id}`}
        >
          <div className="flex items-start justify-between mb-2">
            <div className="font-display text-xl text-[#f4f0ec]">{e.name}</div>
            <Sparkles size={14} className="text-[#d4af37] mt-1" />
          </div>
          <div className="text-sm text-[#a39797] mb-3">{e.tagline}</div>
          <div className="text-[11px] text-[#5a5050]">
            {e.min_players}–{e.max_players} {t("make.playersLabel")} · {Object.keys(e.slots).length} {t("make.setupFields")}
          </div>
        </button>
      ))}
    </div>
  </div>
);

// ---------- Step 2: Setup ----------
const SetupStep = ({
  engine, title, setTitle, theme, setTheme, victimName, setVictimName,
  hostName, setHostName, hostEmail, setHostEmail, slots, setSlots,
  onBack, onNext, t,
}) => {
  const slotMissing = Object.entries(engine.slots).some(
    ([k, cfg]) => cfg.required && !(slots[k] || "").trim(),
  );
  const canAdvance = title.trim() && theme.trim() && victimName.trim() && hostName.trim() && hostEmail.trim() && !slotMissing;

  return (
    <div data-testid="make-step-setup">
      <h2 className="font-display text-2xl text-[#f4f0ec] mb-5">{t("make.setupHeader")}</h2>
      <div className="card-noir p-6 space-y-4">
        <Field label={t("make.caseTitleLabel")} value={title} onChange={setTitle}
          placeholder={t("make.caseTitlePlaceholder")} testid="setup-title" required />
        <Field label={t("make.themeLabel")} value={theme} onChange={setTheme}
          placeholder={t("make.themePlaceholder")} testid="setup-theme" required />
        <div className="grid md:grid-cols-2 gap-4">
          <Field label={t("make.victimLabel")} value={victimName} onChange={setVictimName}
            placeholder={t("make.victimPlaceholder")} testid="setup-victim" required />
          <Field label={t("make.hostNameLabel")} value={hostName} onChange={setHostName}
            placeholder={t("make.hostNamePlaceholder")} testid="setup-host-name" required />
        </div>
        <Field label={t("make.hostEmailLabel")} value={hostEmail} onChange={setHostEmail}
          placeholder={t("make.hostEmailPlaceholder")} testid="setup-host-email" required type="email" />
      </div>

      <h3 className="font-display text-xl text-[#f4f0ec] mt-8 mb-3">{t("make.engineDetails")} · {engine.name}</h3>
      <div className="card-noir p-6 space-y-4">
        {Object.entries(engine.slots).map(([key, cfg]) => (
          <Field
            key={key}
            label={cfg.label + (cfg.required ? " *" : "")}
            placeholder={cfg.placeholder}
            value={slots[key] || ""}
            onChange={(v) => setSlots({ ...slots, [key]: v })}
            testid={`setup-slot-${key}`}
          />
        ))}
      </div>

      <div className="flex justify-between mt-6">
        <button onClick={onBack} className="btn-ghost text-sm" data-testid="setup-back">
          <ArrowLeft size={14} /> {t("make.backBtn")}
        </button>
        <button
          onClick={onNext}
          disabled={!canAdvance}
          className="btn-primary disabled:opacity-50"
          data-testid="setup-next"
        >
          {t("make.nextAddPlayers")} <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
};

// ---------- Step 3: Players + fairness ----------
const PlayersStep = ({
  engine, players, addPlayer, updatePlayer, removePlayer, markCulprit,
  onBack, onNext, draftId, t,
}) => {
  const [fairness, setFairness] = useState(null);
  const [checking, setChecking] = useState(false);
  const culpritCount = players.filter((p) => p.is_culprit).length;
  const minPlayers = engine.min_players;
  const canCheck = players.length >= minPlayers && culpritCount === 1
    && players.every((p) => p.name.trim() && p.role.trim());

  const runFairness = async () => {
    if (!draftId) return;
    setChecking(true);
    try {
      const { data } = await api.post(`/make/draft/${draftId}/fairness`);
      setFairness(data);
      if (data.passed) toast.success(t("make.fairnessPassed"));
    } catch (err) {
      toast.error("Fairness check failed to run.");
    } finally { setChecking(false); }
  };

  return (
    <div data-testid="make-step-players">
      <h2 className="font-display text-2xl text-[#f4f0ec] mb-1">{t("make.playersHeader")}</h2>
      <p className="text-sm text-[#a39797] mb-5">
        {minPlayers}–{engine.max_players} {t("make.playersLabel")}. {t("make.playersIntro")}
        {Object.keys(engine.player_extra || {}).length > 0 && ` ${t("make.engineSpecificIntro")}`}
      </p>

      <div className="space-y-3 mb-5">
        {players.map((p, i) => (
          <PlayerCard
            key={i} index={i} player={p} engine={engine}
            update={(patch) => updatePlayer(i, patch)}
            remove={() => removePlayer(i)}
            markCulprit={() => markCulprit(i)}
            t={t}
          />
        ))}
      </div>

      <div className="flex items-center gap-3 mb-6">
        <button onClick={addPlayer} disabled={players.length >= 12} className="btn-ghost text-sm"
          data-testid="player-add-btn">
          <UserPlus size={14} /> {t("make.addPlayerBtn")} ({players.length}/12)
        </button>
      </div>

      {/* Fairness panel */}
      <div className="card-noir p-5 mb-5">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <div className="font-display text-xl text-[#f4f0ec]">{t("make.fairnessTitle")}</div>
            <div className="text-xs text-[#a39797]">
              {t("make.fairnessIntro")}
            </div>
          </div>
          <button onClick={runFairness} disabled={!canCheck || checking}
            className="btn-primary text-sm disabled:opacity-50" data-testid="fairness-run">
            {checking ? <Loader2 size={14} className="animate-spin" /> : <Hammer size={14} />}
            {t("make.runCheckBtn")}
          </button>
        </div>
        {!canCheck && (
          <div className="text-xs text-[#d4af37]">
            {t("make.fairnessPrereq").replace("{min}", String(minPlayers))}
          </div>
        )}
        {fairness && <FairnessResult result={fairness} t={t} />}
      </div>

      <div className="flex justify-between">
        <button onClick={onBack} className="btn-ghost text-sm" data-testid="players-back">
          <ArrowLeft size={14} /> {t("make.backBtn")}
        </button>
        <button
          onClick={onNext}
          disabled={!fairness?.passed}
          className="btn-primary disabled:opacity-50"
          data-testid="players-next"
        >
          {t("make.previewKitBtn")} <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
};

const PlayerCard = ({ index, player, engine, update, remove, markCulprit, t }) => {
  const extra = engine.player_extra || {};
  return (
    <div className={`card-noir p-5 ${player.is_culprit ? "border-[#d4af37]" : ""}`}
      data-testid={`player-card-${index}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users size={14} className="text-[#a39797]" />
          <span className="text-xs tracking-widest text-[#a39797]">{t("make.playerLabel")} {index + 1}</span>
          {player.is_culprit && (
            <span className="text-[10px] bg-[#d4af37] text-[#1a1413] px-2 py-0.5 rounded font-bold">
              {t("make.theCulpritLabel")}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={markCulprit}
            className={`text-[11px] px-2 py-1 rounded border ${
              player.is_culprit ? "border-[#d4af37] text-[#d4af37]" : "border-[#2d2424] text-[#a39797] hover:border-[#d4af37]"
            }`}
            data-testid={`player-${index}-culprit`}>
            {player.is_culprit ? t("make.culpritBtn") : t("make.markCulpritBtn")}
          </button>
          <button onClick={remove} className="text-[#a39797] hover:text-[#ff6b6b]" data-testid={`player-${index}-remove`}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label={t("make.playerNameLabel")} value={player.name} onChange={(v) => update({ name: v })}
          placeholder={t("make.playerNamePlaceholder")} testid={`player-${index}-name`} />
        <Field label={t("make.playerRoleLabel")} value={player.role} onChange={(v) => update({ role: v })}
          placeholder={t("make.playerRolePlaceholder")} testid={`player-${index}-role`} />
      </div>
      <div className="mt-3">
        <Field label={t("make.playerSecretLabel")} value={player.secret}
          onChange={(v) => update({ secret: v })}
          placeholder={t("make.playerSecretPlaceholder")}
          testid={`player-${index}-secret`} />
      </div>
      <div className="grid md:grid-cols-2 gap-3 mt-3">
        <Field label={t("make.playerSignatureLabel")} value={player.signature_line}
          onChange={(v) => update({ signature_line: v })}
          placeholder={t("make.playerSignaturePlaceholder")}
          testid={`player-${index}-signature`} />
        <Field label={t("make.playerEmailLabel")} type="email"
          value={player.email} onChange={(v) => update({ email: v })}
          placeholder={t("make.playerEmailPlaceholder")} testid={`player-${index}-email`} />
      </div>
      {Object.keys(extra).length > 0 && (
        <div className="mt-4 border-t border-[#2d2424] pt-3">
          <div className="text-[10px] tracking-widest text-[#a39797] mb-2">{t("make.engineSpecificLabel")}</div>
          <div className="grid md:grid-cols-2 gap-3">
            {Object.entries(extra).map(([key, cfg]) => (
              <Field
                key={key}
                label={cfg.label}
                placeholder={cfg.placeholder || ""}
                value={player[key] || ""}
                onChange={(v) => update({ [key]: v })}
                testid={`player-${index}-${key}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const FairnessResult = ({ result, t }) => (
  <div className="mt-3 space-y-2" data-testid="fairness-result">
    <div className={`inline-flex items-center gap-2 text-sm font-bold ${result.passed ? "text-[#4ade80]" : "text-[#ff6b6b]"}`}>
      {result.passed ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
      {result.passed ? t("make.fairnessPassed") : t("make.fairnessFailed")}
    </div>
    <ul className="space-y-1 text-xs">
      {(result.rules || []).map((r, i) => (
        <li key={i} className={r.passed ? "text-[#9fd79f]" : "text-[#ff9f9f]"}>
          {r.passed ? "✓" : "✗"} {r.rule}{r.detail ? ` — ${r.detail}` : ""}
        </li>
      ))}
    </ul>
  </div>
);

// ---------- Step 4: Preview + checkout ----------
const PreviewStep = ({ draftId, engine, hostName, hostEmail, onBack, t }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [previewPlayerIdx, setPreviewPlayerIdx] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState("");
  const [logoBusy, setLogoBusy] = useState(false);

  useEffect(() => {
    api.get(`/make/draft/${draftId}/preview`).then((r) => {
      setPreview(r.data.kit);
    }).catch((err) => {
      toast.error(err?.response?.data?.detail || "Preview failed.");
    }).finally(() => setLoading(false));
    // Load draft to hydrate any existing logo on resume
    api.get(`/make/draft/${draftId}`).then((r) => {
      const d = r.data.draft || {};
      if (d.logo_data_url) setLogoDataUrl(d.logo_data_url);
    }).catch(() => {});
  }, [draftId]);

  const onPickLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(png|jpe?g|gif|webp)$/.test(file.type)) {
      toast.error("Use a PNG, JPG, GIF, or WebP image.");
      return;
    }
    if (file.size > 500_000) {
      toast.error("Logo must be under 500 KB.");
      return;
    }
    setLogoBusy(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = () => rej(new Error("read failed"));
        r.readAsDataURL(file);
      });
      await api.post(`/make/draft/logo`, { draft_id: draftId, logo_data_url: dataUrl });
      setLogoDataUrl(dataUrl);
      toast.success("Logo attached. It'll land on every PDF page.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Logo upload failed.");
    } finally {
      setLogoBusy(false);
    }
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    try {
      await api.delete(`/make/draft/${draftId}/logo`);
      setLogoDataUrl("");
      toast.success("Logo removed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove logo.");
    } finally {
      setLogoBusy(false);
    }
  };

  const checkout = async () => {
    setBuying(true);
    try {
      const { data } = await api.post("/make/checkout", {
        draft_id: draftId, origin_url: window.location.origin,
        host_name: hostName, host_email: hostEmail,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed.");
      setBuying(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-[#8b0000]" size={28} /></div>;
  }
  if (!preview) {
    return <div className="card-noir p-6 text-[#ff6b6b]">Could not load preview.</div>;
  }

  return (
    <div data-testid="make-step-preview">
      <h2 className="font-display text-2xl text-[#f4f0ec] mb-5">{t("make.previewHeader")}</h2>

      <div className="card-noir p-6 mb-6">
        <div className="font-display text-3xl text-[#f4f0ec] mb-2" translate="no">{preview.case_title}</div>
        {preview.theme && <div className="text-sm text-[#d4af37] mb-3">{preview.theme}</div>}
        {preview.premise && <p className="text-[#c7bebe] leading-relaxed mb-4">{preview.premise}</p>}
        <div className="text-xs text-[#a39797]">
          <FileText size={12} className="inline mr-1" />
          {(preview.role_cards || []).length} {t("make.rolesCount")} · {t("make.engineLabel")}: {engine.name}
        </div>
      </div>

      {/* Preview as a Player — kills last-minute buyer's remorse */}
      {preview.role_cards && preview.role_cards.length > 0 && (
        <div className="card-noir p-6 mb-6" data-testid="preview-as-player-card">
          <div className="flex items-center gap-2 mb-2">
            <Eye size={14} className="text-[#d4af37]" />
            <div className="font-display text-xl text-[#f4f0ec]">{t("make.previewAsPlayer")}</div>
          </div>
          <p className="text-xs text-[#a39797] mb-4">{t("make.previewAsPlayerIntro")}</p>
          <div className="text-[10px] tracking-widest text-[#a39797] mb-2">{t("make.previewPickPlayer").toUpperCase()}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {preview.role_cards.map((rc, i) => (
              <button
                key={i}
                onClick={() => setPreviewPlayerIdx(i)}
                className="card-noir p-3 text-left hover:border-[#d4af37] transition-colors"
                data-testid={`preview-player-${i}`}
              >
                <div className="text-sm text-[#f4f0ec] truncate" translate="no">
                  {rc.player_name || `Player ${i + 1}`}
                </div>
                <div className="text-[11px] text-[#a39797] truncate">{rc.archetype || rc.role || ""}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {preview.clues && preview.clues.length > 0 && (
        <div className="card-noir p-6 mb-6">
          <div className="font-display text-xl text-[#f4f0ec] mb-3">{t("make.cluesPreviewTitle")}</div>
          <ul className="space-y-2 text-sm text-[#c7bebe]">
            {preview.clues.slice(0, 3).map((c, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-[#d4af37]">{i + 1}.</span>
                <span>{typeof c === "string" ? c : c.text || c.body || JSON.stringify(c)}</span>
              </li>
            ))}
            {preview.clues.length > 3 && (
              <li className="text-xs text-[#5a5050] italic">+{preview.clues.length - 3} {t("make.moreCluesSuffix")}</li>
            )}
          </ul>
        </div>
      )}

      {/* Final reveal is intentionally hidden until paid */}
      <div className="card-noir p-6 mb-6 bg-gradient-to-br from-[#1a1413] to-[#0a0908]">
        <div className="flex items-center gap-2 mb-2">
          <Lock size={14} className="text-[#d4af37]" />
          <div className="font-display text-xl text-[#f4f0ec]">{t("make.finalRevealTitle")}</div>
        </div>
        <p className="text-sm text-[#a39797]">
          {t("make.finalRevealBody")}
        </p>
      </div>

      {/* Share with co-host — view-only preview link */}
      <div className="card-noir p-6 mb-6" data-testid="share-cohost-card">
        <div className="flex items-center gap-2 mb-2">
          <Mail size={14} className="text-[#d4af37]" />
          <div className="font-display text-xl text-[#f4f0ec]">Share preview with a co-host</div>
        </div>
        <p className="text-xs text-[#a39797] mb-4">
          Send a view-only link to the person planning this with you — they'll see roles, setup, and clues
          but <strong>not</strong> the final reveal. Fairness check must pass first.
        </p>
        <button
          onClick={() => setShareOpen(true)}
          className="btn-ghost text-sm"
          data-testid="share-cohost-open-btn"
        >
          <Mail size={14} /> Share with a co-host
        </button>
      </div>

      {/* Upload logo — B2B / team branding */}
      <div className="card-noir p-6 mb-6" data-testid="logo-upload-card">
        <div className="flex items-center gap-2 mb-2">
          <LogoIcon size={14} className="text-[#d4af37]" />
          <div className="font-display text-xl text-[#f4f0ec]">Add your logo (optional)</div>
        </div>
        <p className="text-xs text-[#a39797] mb-4">
          Brands and teams: upload a logo (PNG/JPG, under 500 KB) and we'll place it on the PDF cover + every
          role card. Perfect for corporate events and fundraisers.
        </p>
        {logoDataUrl ? (
          <div className="flex items-center gap-4 flex-wrap">
            <div className="bg-[#f4f0ec] p-2 rounded border border-[#2d2424]">
              <img
                src={logoDataUrl}
                alt="Your logo preview"
                style={{ maxHeight: 60, maxWidth: 160, display: "block" }}
                data-testid="logo-preview-img"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="btn-ghost text-sm cursor-pointer" data-testid="logo-replace-btn">
                <Upload size={14} /> Replace
                <input type="file" accept="image/png,image/jpeg,image/gif,image/webp"
                  className="hidden" onChange={onPickLogo} disabled={logoBusy} />
              </label>
              <button onClick={removeLogo} disabled={logoBusy}
                className="btn-ghost text-sm text-[#ff6b6b]" data-testid="logo-remove-btn">
                <Trash2 size={14} /> Remove
              </button>
            </div>
          </div>
        ) : (
          <label className="btn-ghost text-sm cursor-pointer inline-flex" data-testid="logo-upload-btn">
            {logoBusy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            Upload logo
            <input type="file" accept="image/png,image/jpeg,image/gif,image/webp"
              className="hidden" onChange={onPickLogo} disabled={logoBusy} />
          </label>
        )}
      </div>

      {/* Checkout */}
      <div className="card-noir p-6 border-[#d4af37]">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="font-display text-2xl text-[#f4f0ec]">{t("make.unlockCardTitle")}</div>
            <div className="text-sm text-[#a39797]">{t("make.unlockCardIntro")}</div>
          </div>
          <div className="text-right">
            <div className="font-display text-4xl text-[#d4af37]" translate="no">$12</div>
            <div className="text-xs text-[#a39797]">{t("make.oneTimeLabel")}</div>
          </div>
        </div>
        <button onClick={checkout} disabled={buying}
          className="btn-primary w-full mt-4 justify-center"
          data-testid="make-checkout-btn">
          {buying ? <Loader2 size={14} className="animate-spin" /> : <Skull size={14} />}
          {t("make.buyBtn")}
        </button>
        <div className="text-[11px] text-[#5a5050] mt-2 text-center">
          {t("make.checkoutFooter").replace("{email}", hostEmail || "your inbox")}
        </div>
      </div>

      <div className="flex justify-between mt-6">
        <button onClick={onBack} className="btn-ghost text-sm" data-testid="preview-back">
          <ArrowLeft size={14} /> {t("make.editPlayersBtn")}
        </button>
      </div>

      {/* Player-view modal */}
      {previewPlayerIdx != null && preview.role_cards && (
        <PlayerViewModal
          role={preview.role_cards[previewPlayerIdx]}
          caseTitle={preview.case_title}
          onClose={() => setPreviewPlayerIdx(null)}
          t={t}
        />
      )}

      {/* Share-with-co-host modal */}
      {shareOpen && (
        <ShareCoHostModal
          draftId={draftId}
          defaultSender={hostName}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
};

// ---------- Share-with-co-host modal ----------
const ShareCoHostModal = ({ draftId, defaultSender, onClose }) => {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [sender, setSender] = useState(defaultSender || "");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null); // { preview_url }
  const [copied, setCopied] = useState(false);

  const send = async () => {
    const trimmed = (email || "").trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/make/draft/${draftId}/share`, {
        cohost_email: trimmed,
        sender_name: sender,
        note,
        origin_url: window.location.origin,
      });
      setSent({ preview_url: data.preview_url });
      toast.success("Preview link emailed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the preview.");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!sent?.preview_url) return;
    try {
      await navigator.clipboard.writeText(sent.preview_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Could not copy. Select the link manually.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      data-testid="share-modal"
    >
      <div
        className="card-noir p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-2xl text-[#f4f0ec]">Share preview</div>
          <button onClick={onClose} className="text-[#a39797] hover:text-[#f4f0ec]"
            data-testid="share-modal-close">
            <X size={18} />
          </button>
        </div>

        {sent ? (
          <div>
            <div className="text-sm text-[#c7bebe] mb-2">
              Sent. Your co-host will get the email within a minute. You can also share this link directly:
            </div>
            <div className="bg-[#0a0908] border border-[#2d2424] rounded p-2 text-xs text-[#d4af37] break-all mb-3"
              data-testid="share-preview-url">
              {sent.preview_url}
            </div>
            <div className="flex gap-2">
              <button onClick={copyLink} className="btn-ghost text-sm" data-testid="share-copy-btn">
                {copied ? <CheckCircle2 size={14} className="text-green-400" /> : <Save size={14} />}
                {copied ? "Copied" : "Copy link"}
              </button>
              <button onClick={onClose} className="btn-primary text-sm" data-testid="share-done-btn">
                Done
              </button>
            </div>
            <p className="text-[11px] text-[#5a5050] mt-3">
              The link shows roles, clues, and setup — the final reveal stays locked until you pay $12.
            </p>
          </div>
        ) : (
          <div>
            <label className="block mb-3">
              <div className="text-xs text-[#a39797] mb-1">Co-host email</div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="cohost@email.com"
                className="input-noir w-full"
                data-testid="share-email-input"
                autoFocus
              />
            </label>
            <label className="block mb-3">
              <div className="text-xs text-[#a39797] mb-1">Your name (optional)</div>
              <input
                type="text"
                value={sender}
                onChange={(e) => setSender(e.target.value)}
                placeholder="You"
                className="input-noir w-full"
                data-testid="share-sender-input"
                maxLength={60}
              />
            </label>
            <label className="block mb-4">
              <div className="text-xs text-[#a39797] mb-1">Personal note (optional)</div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Tell them what to look at…"
                className="input-noir w-full"
                data-testid="share-note-input"
                rows={3}
                maxLength={400}
              />
            </label>
            <div className="flex gap-2 justify-end">
              <button onClick={onClose} className="btn-ghost text-sm" data-testid="share-cancel-btn">
                Cancel
              </button>
              <button onClick={send} disabled={busy}
                className="btn-primary text-sm" data-testid="share-send-btn">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                Send preview
              </button>
            </div>
            <p className="text-[11px] text-[#5a5050] mt-3">
              Fairness check must pass first. The reveal stays locked until payment.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Player-view mock modal ----------
// Mirrors what the actual player page renders at /play/:token so hosts can
// commit to buying without wondering what their guests will see.
const PlayerViewModal = ({ role, caseTitle, onClose, t }) => {
  const voice = role.voice || role.role || "";
  const energy = role.energy_tag || role.looks_suspicious || "";
  const signatureLine = role.signature_line || "";
  const secret = role.secret || role.accused_secret || "";
  const blurb = role.blurb || "";

  return (
    <div
      className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="player-view-modal"
    >
      <div
        className="relative bg-[#0a0908] border border-[#2d2424] rounded-xl max-w-sm w-full p-6 my-10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Phone frame cue */}
        <div className="text-[10px] tracking-widest text-[#5a5050] mb-3 text-center">
          📱 PREVIEW · YOUR PHONE WILL LOOK LIKE THIS
        </div>

        {/* Case title (small, like the player app header) */}
        <div className="font-hand text-[#d4af37] text-sm text-center rotate-[-1deg] mb-1">
          wasntme.party
        </div>
        <div className="font-display text-sm text-[#a39797] text-center mb-5" translate="no">
          {caseTitle}
        </div>

        {/* Role card body */}
        <div className="text-xs tracking-widest text-[#a39797] mb-1">{t("make.playerViewTitle").toUpperCase()}</div>
        <div className="font-display text-3xl text-[#f4f0ec] mb-1" translate="no">{role.archetype || role.role}</div>
        <div className="text-xs text-[#a39797] mb-4" translate="no">Played by {role.player_name}</div>

        {blurb && (
          <p className="text-sm text-[#c7bebe] leading-relaxed mb-4">{blurb}</p>
        )}

        {voice && (
          <div className="mb-3">
            <div className="text-[10px] tracking-widest text-[#a39797]">{t("make.playerViewVibeLabel")}</div>
            <div className="text-sm text-[#f4f0ec]">{voice}</div>
          </div>
        )}

        {signatureLine && (
          <div className="mb-3 border-l-2 border-[#d4af37] pl-3">
            <div className="text-[10px] tracking-widest text-[#a39797]">{t("make.playerViewSignatureLabel")}</div>
            <div className="text-sm italic text-[#f4f0ec]">"{signatureLine}"</div>
          </div>
        )}

        {energy && (
          <div className="mb-3">
            <div className="text-[10px] tracking-widest text-[#a39797]">Energy</div>
            <div className="text-sm text-[#f4f0ec]">{energy}</div>
          </div>
        )}

        {secret && (
          <div className="mt-4 p-3 bg-[#2d1414] border border-[#4f1a1a] rounded-md">
            <div className="text-[10px] tracking-widest text-[#ff9f9f] mb-1 flex items-center gap-1">
              <Lock size={10} /> {t("make.playerViewSecretLabel")}
            </div>
            <div className="text-sm text-[#f4d4d4]">{secret}</div>
          </div>
        )}

        <button
          onClick={onClose}
          className="btn-primary text-sm w-full justify-center mt-5"
          data-testid="player-view-close"
        >
          <X size={14} /> {t("make.closePreview")}
        </button>
      </div>
    </div>
  );
};

// ---------- Field helper ----------
const Field = ({ label, value, onChange, placeholder, testid, type = "text", required }) => (
  <div>
    <label className="block text-xs tracking-widest text-[#a39797] mb-1">{label.toUpperCase()}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      className="w-full bg-[#0a0908] border border-[#2d2424] rounded-md px-3 py-2 text-[#f4f0ec] text-sm focus:outline-none focus:border-[#d4af37]"
      data-testid={testid}
    />
  </div>
);

export default Make;
